import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { User, Shield, Bell, Palette, Save, Loader2, CheckCircle, Upload, Key, Copy, Trash2, Plus, Clock, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const { user, setUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [profile, setProfile] = useState({
    name: user?.name || '',
    organization: user?.organization || '',
  });
  const [sessionTimeout, setSessionTimeout] = useState('7');
  const [apiKeys, setApiKeys] = useState([]);
  const [showNewApiKey, setShowNewApiKey] = useState(false);
  const [newApiKeyName, setNewApiKeyName] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);
  const [newKeyValue, setNewKeyValue] = useState('');
  const [notifications, setNotifications] = useState({
    critical_vulnerabilities: true,
    scan_completion: true,
    sla_deadlines: true,
    compliance_alerts: true,
    weekly_digest: false,
    darkweb_alerts: true,
    incident_updates: true,
  });
  const [savingNotifs, setSavingNotifs] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchApiKeys();
    fetchNotificationPrefs();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const res = await axios.get(`${API}/settings/api-keys`, { withCredentials: true });
      setApiKeys(res.data);
    } catch {}
  };

  const fetchNotificationPrefs = async () => {
    try {
      const res = await axios.get(`${API}/settings/notifications`, { withCredentials: true });
      if (res.data) setNotifications(res.data);
    } catch {}
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/profile`, profile, { withCredentials: true });
      setUser(prev => ({ ...prev, ...res.data }));
      setSaved(true);
      toast.success('Profile saved');
      setTimeout(() => setSaved(false), 2000);
    } catch { toast.error('Failed to save profile'); }
    finally { setSaving(false); }
  };

  const uploadProfileImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/settings/profile/image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUser(prev => ({ ...prev, picture: res.data.picture }));
      toast.success('Profile image updated');
    } catch { toast.error('Failed to upload image'); }
    finally { setUploadingImage(false); }
  };

  const saveSessionTimeout = async (value) => {
    setSessionTimeout(value);
    try {
      await axios.put(`${API}/settings/session-timeout`, { timeout_days: parseInt(value) }, { withCredentials: true });
      toast.success(`Session timeout set to ${value} days`);
    } catch { toast.error('Failed to update session timeout'); }
  };

  const createApiKey = async () => {
    if (!newApiKeyName.trim()) return;
    setCreatingKey(true);
    try {
      const res = await axios.post(`${API}/settings/api-keys`, { name: newApiKeyName }, { withCredentials: true });
      setApiKeys(prev => [...prev, res.data]);
      setNewKeyValue(res.data.key);
      setNewApiKeyName('');
      toast.success('API key created. Copy it now - it won\'t be shown again!');
    } catch { toast.error('Failed to create API key'); }
    finally { setCreatingKey(false); }
  };

  const deleteApiKey = async (keyId) => {
    if (!window.confirm('Delete this API key? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/settings/api-keys/${keyId}`, { withCredentials: true });
      setApiKeys(prev => prev.filter(k => k.key_id !== keyId));
      toast.success('API key deleted');
    } catch { toast.error('Failed to delete API key'); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const saveNotifications = async () => {
    setSavingNotifs(true);
    try {
      await axios.put(`${API}/settings/notifications`, notifications, { withCredentials: true });
      toast.success('Notification preferences saved');
    } catch { toast.error('Failed to save notifications'); }
    finally { setSavingNotifs(false); }
  };

  const toggleNotification = (key) => {
    setNotifications(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight" data-testid="settings-title">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your account and preferences</p>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="mb-6" data-testid="settings-tabs">
          <TabsTrigger value="profile"><User className="h-4 w-4 mr-2" /> Profile</TabsTrigger>
          <TabsTrigger value="security"><Shield className="h-4 w-4 mr-2" /> Security</TabsTrigger>
          <TabsTrigger value="appearance"><Palette className="h-4 w-4 mr-2" /> Appearance</TabsTrigger>
          <TabsTrigger value="notifications"><Bell className="h-4 w-4 mr-2" /> Notifications</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <Card data-testid="profile-card">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Profile Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-primary/20 text-primary text-xl">{initials}</AvatarFallback>
                  </Avatar>
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    className="hidden"
                    onChange={uploadProfileImage}
                    data-testid="profile-image-input"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    data-testid="upload-profile-image-btn"
                  >
                    {uploadingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  </Button>
                </div>
                <div>
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="capitalize text-xs">{user?.role}</Badge>
                    <Badge variant="outline" className="capitalize text-xs">{user?.team || 'security'}</Badge>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name</Label>
                  <Input value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} data-testid="profile-name-input" />
                </div>
                <div>
                  <Label>Organization</Label>
                  <Input value={profile.organization} onChange={e => setProfile(p => ({ ...p, organization: e.target.value }))} placeholder="Your company" data-testid="profile-org-input" />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Button onClick={saveProfile} disabled={saving} className="gap-2" data-testid="save-profile-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : saved ? <CheckCircle className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                  {saved ? 'Saved!' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security">
          <div className="space-y-4">
            <Card data-testid="security-card">
              <CardHeader>
                <CardTitle className="text-lg font-heading">Security Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div>
                    <p className="font-medium text-sm">Two-Factor Authentication</p>
                    <p className="text-xs text-muted-foreground">Add extra security to your account</p>
                  </div>
                  <Switch data-testid="2fa-toggle" />
                </div>
                
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium text-sm">Session Timeout</p>
                      <p className="text-xs text-muted-foreground">Auto-logout after inactivity</p>
                    </div>
                  </div>
                  <Select value={sessionTimeout} onValueChange={saveSessionTimeout}>
                    <SelectTrigger className="w-[140px]" data-testid="session-timeout-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 day</SelectItem>
                      <SelectItem value="3">3 days</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* API Key Management */}
            <Card data-testid="api-keys-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Key className="h-5 w-5" /> API Key Management
                  </CardTitle>
                  <Dialog open={showNewApiKey} onOpenChange={(open) => { setShowNewApiKey(open); if (!open) setNewKeyValue(''); }}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="gap-1" data-testid="new-api-key-btn">
                        <Plus className="h-4 w-4" /> New Key
                      </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="api-key-dialog">
                      <DialogHeader>
                        <DialogTitle>Create API Key</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        {newKeyValue ? (
                          <div className="space-y-3">
                            <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                              <p className="text-sm font-medium text-green-500 mb-2">API Key Created!</p>
                              <p className="text-xs text-muted-foreground mb-3">Copy this key now. You won't be able to see it again.</p>
                              <div className="flex items-center gap-2">
                                <code className="flex-1 p-2 rounded bg-muted text-xs font-mono break-all">{newKeyValue}</code>
                                <Button size="icon" variant="outline" onClick={() => copyToClipboard(newKeyValue)} data-testid="copy-api-key">
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            <Button className="w-full" onClick={() => { setShowNewApiKey(false); setNewKeyValue(''); }}>Done</Button>
                          </div>
                        ) : (
                          <>
                            <div>
                              <Label>Key Name</Label>
                              <Input 
                                value={newApiKeyName} 
                                onChange={e => setNewApiKeyName(e.target.value)} 
                                placeholder="e.g., CI/CD Pipeline"
                                data-testid="api-key-name-input"
                              />
                            </div>
                            <Button onClick={createApiKey} disabled={creatingKey || !newApiKeyName.trim()} className="w-full" data-testid="create-api-key-btn">
                              {creatingKey ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                              Create API Key
                            </Button>
                          </>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-2">
                    {apiKeys.map(key => (
                      <div key={key.key_id} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`api-key-${key.key_id}`}>
                        <div className="flex items-center gap-3">
                          <Key className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">{key.name}</p>
                            <p className="text-xs text-muted-foreground">
                              Created {new Date(key.created_at).toLocaleDateString()} • 
                              Last used: {key.last_used ? new Date(key.last_used).toLocaleDateString() : 'Never'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <code className="text-xs text-muted-foreground font-mono">{key.key_preview}</code>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteApiKey(key.key_id)} data-testid={`delete-key-${key.key_id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {apiKeys.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground text-sm">
                        <Key className="h-8 w-8 mx-auto mb-2 opacity-30" />
                        No API keys yet. Create one to access the API programmatically.
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="appearance">
          <Card data-testid="appearance-card">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Appearance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div>
                  <p className="font-medium text-sm">Dark Mode</p>
                  <p className="text-xs text-muted-foreground">Toggle dark/light theme</p>
                </div>
                <Switch checked={theme === 'dark'} onCheckedChange={toggleTheme} data-testid="dark-mode-toggle" />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications">
          <Card data-testid="notifications-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-heading">Notification Preferences</CardTitle>
                <Button size="sm" onClick={saveNotifications} disabled={savingNotifs} className="gap-1" data-testid="save-notifs-btn">
                  {savingNotifs ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { key: 'critical_vulnerabilities', label: 'Critical Vulnerabilities', desc: 'Get alerted when critical vulnerabilities are detected' },
                { key: 'scan_completion', label: 'Scan Completion', desc: 'Receive notification when scans complete' },
                { key: 'sla_deadlines', label: 'SLA Deadlines', desc: 'Alerts for approaching remediation deadlines' },
                { key: 'compliance_alerts', label: 'Compliance Alerts', desc: 'Notifications for compliance status changes' },
                { key: 'darkweb_alerts', label: 'Darkweb Alerts', desc: 'Alerts for darkweb monitoring findings' },
                { key: 'incident_updates', label: 'Incident Updates', desc: 'Updates on security incident status' },
                { key: 'weekly_digest', label: 'Weekly Digest', desc: 'Weekly summary of security posture' },
              ].map(item => (
                <div key={item.key} className="flex items-center justify-between p-4 rounded-lg border">
                  <div>
                    <p className="font-medium text-sm">{item.label}</p>
                    <p className="text-xs text-muted-foreground">{item.desc}</p>
                  </div>
                  <Switch 
                    checked={notifications[item.key]} 
                    onCheckedChange={() => toggleNotification(item.key)}
                    data-testid={`notif-${item.key}`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
